import classNames from "classnames";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IoIosGitMerge } from "react-icons/io";

import { AvatarPlaceholder } from "../../../../components/avatar/AvatarPlaceholder";
import Button from "../../../../components/button/Button";
import CustomSelect from "../../../../components/select/Select";
import { getInitialsFromFullName } from "../../../../constants/constants";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useGetSearchCustomersQuery,
  useMergedContactsMutation,
} from "../../../../slices/ContactsSlice";
import { SelectOptionType } from "../../../../types/types";

type Props = {
  customerId: string;
  name: string;
  isActive: boolean;
  onClose: () => void;
  type: "contact" | "organization";
};

export const MergeContactModal: FC<Props> = ({
  customerId,
  name,
  isActive,
  onClose,
  type,
}) => {
  const [search, setSearch] = useState("");

  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const userId = user.id;

  const { data: contacts, isFetching: isLoadingContacts } =
    useGetSearchCustomersQuery(
      {
        orgId,
        userId,
        searchQuery: search || "ab",
        isActive: true,
      },
      {
        skip: !orgId || !userId,
      },
    );
  const [isActiveMerge, setIsActiveMerge] = useState(false);

  const [mergeContacts, { isLoading: isLoadingMerge, isSuccess }] =
    useMergedContactsMutation();

  const [selectedContact, setSelectedContact] =
    useState<SelectOptionType | null>(null);

  const options = useMemo<SelectOptionType[]>(() => {
    if (type === "contact") {
      const data: SelectOptionType[] =
        contacts
          ?.filter((i) => i.contactId !== null)
          .map((item) => ({
            label: item.customerName,
            value: item.customerId || "",
            avatar: getInitialsFromFullName(item.customerName),
          })) || [];
      return data?.filter((item) => item?.value !== customerId) || [];
    }

    if (type === "organization") {
      const data: SelectOptionType[] =
        contacts
          ?.filter((i) => i.businessContactId !== null)
          .map((item) => ({
            label: `${item?.customerName}`,
            value: item.customerId || "",
            avatar: getInitialsFromFullName(item?.customerName || ""),
            avatarColor: "purple",
          })) || [];

      return data?.filter((item) => item?.value !== customerId) || [];
    }

    return [];
  }, [contacts, customerId, type]);

  const handleSelectContact = useCallback((newValue: unknown) => {
    if (Array.isArray(newValue)) {
      return;
    }

    setSelectedContact(newValue as SelectOptionType | null);
  }, []);

  const handleOpenMergeConfirmation = useCallback(() => {
    if (selectedContact) {
      setIsActiveMerge(true);
    }
  }, [selectedContact]);

  const handleMergeContacts = useCallback(() => {
    if (selectedContact?.value) {
      void mergeContacts({
        mergeCustomerId: selectedContact?.value || "",
        mergeToCustomerId: customerId,
      });
    }
  }, [customerId, mergeContacts, selectedContact?.value]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <div
      className={classNames("modal", isActive ? "modal-open" : "modal-close")}>
      <div className={"modal-box"}>
        <div className={"mb-4 text-2xl font-semibold"}>Merge contacts</div>
        <div className={"mb-4"}>
          <div className={"mb-2 text-sm font-semibold"}>Primary contact:</div>
          <div
            className={
              "flex items-center gap-4 rounded-[8px] border border-gray-300 p-2"
            }>
            <AvatarPlaceholder
              size={"super-small"}
              label={getInitialsFromFullName(name)}
              type={type === "contact" ? "blue" : "purple"}
            />
            <div className={"truncate text-sm font-semibold text-gray-800"}>
              {name}
            </div>
          </div>
        </div>
        <div className={"mb-4"}>
          <div className={"mb-2 text-sm font-semibold"}>
            Secondary contact (To be deleted)
          </div>
          <div>
            <CustomSelect
              options={options}
              onChange={handleSelectContact}
              inputValue={search}
              onChangeInput={(e) => setSearch(e)}
              value={selectedContact}
              isLoading={isLoadingContacts}
              withAvatar
              withMenu
              selectStyle={{ fullWidth: true }}
            />
          </div>
        </div>
        <div className={"mb-2 text-sm font-semibold text-gray-500"}>
          <ul className={"list-disc pl-5"}>
            <li>
              The secondary contact work items, notes, documents, invoices and
              payments will move to the primary contact.
            </li>
            <li>
              The primary contact info section (address, phone number etc.) will
              be kept.
            </li>
            <li>The secondary contact will be permanently deleted.</li>
          </ul>
        </div>
        <div className={"mb-4 text-sm font-bold text-gray-800"}>
          This cannot be undone. Are you sure you want to continue?
        </div>
        <div className={"flex justify-end gap-4"}>
          <Button
            label={isLoadingMerge ? "Loading" : "Continue"}
            onClick={handleOpenMergeConfirmation}
            disabled={isLoadingMerge || !selectedContact}
          />
          <Button label={"Cancel"} onClick={onClose} colorType={"outline"} />
        </div>
      </div>
      {isActiveMerge && (
        <div
          className={classNames(
            "modal",
            isActive ? "modal-open" : "modal-close",
          )}>
          <div className={"w-[400px] rounded-[12px] bg-white p-6"}>
            <div className={""}>
              <div
                className={
                  "mb-4 flex h-[35px] w-[35px] items-center justify-center rounded-[50%] bg-blue-100"
                }>
                <IoIosGitMerge className={"text-blue-600"} />
              </div>
              <div
                className={"font-Manrope text-xl font-semibold text-[#1D2C3B]"}>
                Merge contacts?
              </div>
              <div
                className={
                  "mb-4 font-Manrope text-sm font-medium text-[#5F758F]"
                }>
                Are you sure you want to merge these 2 contacts?
              </div>
            </div>
            <div className={"flex justify-end gap-4"}>
              <Button
                label={isLoadingMerge ? "Loading" : "Continue"}
                onClick={handleMergeContacts}
                disabled={isLoadingMerge || !selectedContact}
              />
              <Button
                label={"Cancel"}
                onClick={() => setIsActiveMerge(false)}
                colorType={"outline"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
